import CloseIcon from "@mui/icons-material/Close";
import { CircularProgress, Dialog, DialogContent, DialogTitle, Grid, IconButton } from "@mui/material";
import { makeStyles } from "@mui/styles";
import _ from "lodash";
import { DateTime } from "luxon";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import {
  fetchLatestBidAllotmentEventsByReferenceNumber,
  fetchLatestBidGroupEmpEventsByReferenceNumber,
} from "../../api/bid";
import FreedayBidPDFExport from "../../pages/bid/FreedayBidPDFExport";
import IAMDATBidPDFExport from "../../pages/bid/IAMDATBidPDFExport";
import IAMFTOBidPDFExport from "../../pages/bid/IAMFTOBidPDFExport";
import NCBidPDFExport from "../../pages/bid/NCBidPDFExport";
import TWUDATFTOBidPDFExport from "../../pages/bid/TWUDATFTOBidPDFExport";
import { generateIAMDATDayStateRows } from "../../util/bid/IAMDATBidUtils";
import { generateNCDayStateRows } from "../../util/bid/NonContractBidUtils";
import { generateDATFTODayStateRows } from "../../util/bid/TWUDATFTOBidUtils";
import { BID_TYPE, NC_BID_AWARD_TYPE } from "../../util/bid/bidConstants";
import {
  calcReliefAllotmentInitialState,
  calcStandardAllotmentInitialState,
  generateDayStateRows,
} from "../../util/bid/freedayBidUtils";
import { calcAllotmentInitialState, generateFTODayStateRows } from "../../util/bid/ftoBidUtils";
import {
  getFormattedAllotmentDayState,
  getFormattedIAMDATAllotmentDayState,
  getFormattedIAMFTOAllotmentDayState,
  getFormattedNCBidAllotmentDayState,
  getFormattedTWUDATFTOAllotmentDayState,
} from "../../util/calendar";
import { copyJsonObject } from "../../util/commonUtils";
import { MY_BID_RESULTS_DIALOG_MESSAGE, MY_BID_RESULTS_DIALOG_UNAWARDED_MESSAGE } from "../../util/messageConstants";
import SlideUpTransition from "../fundamentals/SlideUpTransition";

const useStyles = makeStyles((theme) => ({
  circularProgress: {
    marginBottom: "10px",
  },

  bidScheduleDay: {
    fontWeight: "bold",
    color: theme.palette.primary.swaBlue,
  },
  bidSchedules: {
    color: theme.palette.primary.swaBlue,
  },
  closeIcon: {
    alignSelf: "flex-end",
    color: theme.palette.grey[500],
    marginRight: "20px",
    marginTop: "5px",
  },
  noBidMsg: {
    color: theme.palette.primary.swaBlue,
    fontWeight: "bold",
  },
}));

const MyTimeOffBidResultsDialog = (props) => {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(true);
  const [awardedEmpData, setAwardedEmpData] = useState(null);
  const [bidAllotments, setBidAllotments] = useState([]);
  const [calcStandardAllotments, setCalcStandardAllotments] = useState(
    copyJsonObject(calcStandardAllotmentInitialState)
  );
  const [calcReliefAllotments, setCalcReliefAllotments] = useState(copyJsonObject(calcReliefAllotmentInitialState));
  const [calcDailyAllotments, setCalcDailyAllotments] = useState(copyJsonObject(calcAllotmentInitialState));

  useEffect(() => {
    if (isLoading) {
      fetchAwardedAllotment();
    }
    // eslint-disable-next-line
  }, [isLoading]);

  const fetchAwardedAllotment = () => {
    fetchLatestBidGroupEmpEventsByReferenceNumber(
      props.selectedAwardedBidChoiceData.eid,
      props.selectedAwardedBidChoiceData.referenceNumber
    )
      .then((res) => res.json())
      .then((response) => {
        if (response.events) {
          const empData = response.events.find((emp) => emp.eid === props.selectedAwardedBidChoiceData.eid);
          if (_.has(empData, "awardType")) {
            setAwardedEmpData(empData);
          }

          const initialBidGroup = response.events;
          fetchLatestBidAllotmentEventsByReferenceNumber(
            props.selectedAwardedBidChoiceData.eid,
            props.selectedAwardedBidChoiceData.referenceNumber
          )
            .then((res) => res.json())
            .then((response) => {
              if (response?.events) {
                if (props.selectedAwardedBidChoiceData.bidType === BID_TYPE.FREEDAY) {
                  const initialStandardDayState = getFormattedAllotmentDayState(
                    response.events.standardAllotments,
                    props.selectedAwardedBidChoiceData.schedMonth,
                    props.selectedAwardedBidChoiceData.schedYear
                  );
                  const initialReliefDayState = getFormattedAllotmentDayState(
                    response.events.reliefAllotments,
                    props.selectedAwardedBidChoiceData.schedMonth,
                    props.selectedAwardedBidChoiceData.schedYear
                  );
                  setBidAllotments(
                    generateDayStateRows(initialStandardDayState, initialReliefDayState, initialBidGroup)
                  );
                  setCalcReliefAllotments(response.events.calcReliefAllotments);
                  setCalcStandardAllotments(response.events.calcStandardAllotments);
                } else if (props.selectedAwardedBidChoiceData.bidType === BID_TYPE.FTO) {
                  const initialDayState = getFormattedIAMFTOAllotmentDayState(
                    response.events.allotments,
                    props.selectedAwardedBidChoiceData.schedMonth,
                    props.selectedAwardedBidChoiceData.schedYear
                  );
                  setBidAllotments(generateFTODayStateRows(initialDayState, initialBidGroup));
                  setCalcDailyAllotments(response.events.allotments);
                } else if (props.selectedAwardedBidChoiceData.bidType === BID_TYPE.TWU_DAT_FTO) {
                  const initialDayState = getFormattedTWUDATFTOAllotmentDayState(
                    response.events.allotments,
                    props.selectedAwardedBidChoiceData.schedMonth,
                    props.selectedAwardedBidChoiceData.schedYear
                  );
                  setBidAllotments(generateDATFTODayStateRows(initialDayState, initialBidGroup));
                  setCalcDailyAllotments(response.events.allotments);
                } else if (props.selectedAwardedBidChoiceData.bidType === BID_TYPE.IAM_DAT) {
                  const initialDayState = getFormattedIAMDATAllotmentDayState(
                    response.events.allotments,
                    props.selectedAwardedBidChoiceData.schedMonth,
                    props.selectedAwardedBidChoiceData.schedYear
                  );
                  setBidAllotments(generateIAMDATDayStateRows(initialDayState, initialBidGroup));
                  setCalcDailyAllotments(response.events.allotments);
                } else if (
                  props.selectedAwardedBidChoiceData.bidType === BID_TYPE.NC_PTO_FTO ||
                  props.selectedAwardedBidChoiceData.bidType === BID_TYPE.NC_PTO_GOC_HOL
                ) {
                  const initialDayState = getFormattedNCBidAllotmentDayState(
                    response.events.allotments,
                    props.selectedAwardedBidChoiceData.schedMonth,
                    props.selectedAwardedBidChoiceData.schedYear
                  );
                  setBidAllotments(generateNCDayStateRows(initialDayState, initialBidGroup));
                  setCalcDailyAllotments(response.events.allotments);
                } else {
                  console.error("Unknown bidType");
                }

                setIsLoading(false);
              } else {
                throw Error();
              }
            })
            .catch((err) => {
              console.error(`Bid Error: %j`, err);
            });
        } else {
          throw Error();
        }
      })
      .catch((err) => {
        console.error(`Bid Error: %j`, err);
      });
  };

  const renderMyBidResultsDialogTitle = () => {
    return `${props.selectedAwardedBidChoiceData.station} | ${
      props.selectedAwardedBidChoiceData.workgroup
    } | ${props.selectedAwardedBidChoiceData?.schedMonth?.substring(0, 3)} ${
      props.selectedAwardedBidChoiceData?.schedYear
    } ${props.selectedAwardedBidChoiceData.bidType}`;
  };

  const renderMyBidResultsDialogContent = () => {
    if (isLoading) {
      return <CircularProgress className={classes.circularProgress} />;
    } else if (!awardedEmpData || awardedEmpData.awardType === NC_BID_AWARD_TYPE.NO_BID) {
      // Ineligible/Removed/ employee awarded with NB
      return <div className={classes.noBidMsg}>{MY_BID_RESULTS_DIALOG_MESSAGE}</div>;
    } else if (
      awardedEmpData.awardType === NC_BID_AWARD_TYPE.NOT_ENOUGH_CHOICES ||
      awardedEmpData.awardType === NC_BID_AWARD_TYPE.ZERO_BALANCE
    ) {
      return <div className={classes.noBidMsg}>{MY_BID_RESULTS_DIALOG_UNAWARDED_MESSAGE}</div>;
    } else {
      return <>{renderAwardedBidSchedule()}</>;
    }
  };

  const renderAwardedBidSchedule = () => {
    return awardedEmpData.awardedDays.map((awardedDay) => {
      return (
        <Grid container item className={classes.bidSchedules} key={awardedDay.date}>
          <Grid container item direction="row">
            <Grid item xs={3} className={classes.bidScheduleDay}>
              {DateTime.fromISO(awardedDay.date).toFormat("ccc")}
            </Grid>
            <Grid item xs={4}>
              {DateTime.fromISO(awardedDay.date).toFormat("MM/dd/yyyy")}
            </Grid>
            <Grid item>{awardedDay.timeOffType && <Grid item>{awardedDay.timeOffType}</Grid>}</Grid>
          </Grid>
        </Grid>
      );
    });
  };

  const renderPDFExportButton = () => {
    if (props.selectedAwardedBidChoiceData.bidType === BID_TYPE.FREEDAY) {
      return (
        <FreedayBidPDFExport
          bidChoiceData={props.selectedAwardedBidChoiceData}
          buttonVariant="contained"
          bidAllotments={bidAllotments}
          isEmployeeAwardExport={true}
          calcStandardAllotments={calcStandardAllotments}
          calcReliefAllotments={calcReliefAllotments}
          isMyBidResultsDialogLoading={isLoading}
        />
      );
    } else if (props.selectedAwardedBidChoiceData.bidType === BID_TYPE.FTO) {
      return (
        <IAMFTOBidPDFExport
          bidChoiceData={props.selectedAwardedBidChoiceData}
          buttonVariant="contained"
          bidAllotments={bidAllotments}
          isEmployeeAwardExport={true}
          calcDailyAllotments={calcDailyAllotments}
          isMyBidResultsDialogLoading={isLoading}
        />
      );
    } else if (props.selectedAwardedBidChoiceData.bidType === BID_TYPE.TWU_DAT_FTO) {
      return (
        <TWUDATFTOBidPDFExport
          bidChoiceData={props.selectedAwardedBidChoiceData}
          buttonVariant="contained"
          bidAllotments={bidAllotments}
          isEmployeeAwardExport={true}
          isMyBidResultsDialogLoading={isLoading}
        />
      );
    } else if (props.selectedAwardedBidChoiceData.bidType === BID_TYPE.IAM_DAT) {
      return (
        <IAMDATBidPDFExport
          bidChoiceData={props.selectedAwardedBidChoiceData}
          buttonVariant="contained"
          bidAllotments={bidAllotments}
          isEmployeeAwardExport={true}
          isMyBidResultsDialogLoading={isLoading}
        />
      );
    } else if (
      props.selectedAwardedBidChoiceData.bidType === BID_TYPE.NC_PTO_FTO ||
      props.selectedAwardedBidChoiceData.bidType === BID_TYPE.NC_PTO_GOC_HOL
    ) {
      return (
        <NCBidPDFExport
          bidChoiceData={props.selectedAwardedBidChoiceData}
          buttonVariant="contained"
          bidAllotments={bidAllotments}
          isEmployeeAwardExport={true}
          isMyBidResultsDialogLoading={isLoading}
        />
      );
    } else {
      return <></>;
    }
  };

  return (
    <Dialog
      PaperProps={{
        sx: {
          width: "350px",
          maxWidth: "90vw",
          margin: "auto",
          whiteSpace: "pre-line",
        },
      }}
      open={!!props.selectedAwardedBidChoiceData}
      onClose={props.onClickDialogClose}
      data-testid="my-bid-results-dialog"
      TransitionComponent={SlideUpTransition}
      keepMounted
    >
      <IconButton aria-label="close" onClick={props.onClickDialogClose} className={classes.closeIcon}>
        <CloseIcon />
      </IconButton>
      <DialogTitle>{renderMyBidResultsDialogTitle()}</DialogTitle>
      <DialogContent>
        <Grid container direction="column">
          {renderMyBidResultsDialogContent()}
        </Grid>
        {renderPDFExportButton()}
      </DialogContent>
    </Dialog>
  );
};

MyTimeOffBidResultsDialog.propTypes = {
  selectedAwardedBidChoiceData: PropTypes.object.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
};

export default MyTimeOffBidResultsDialog;
